import { useMemo } from "react";
import type { FormContent, MarkGroupMode } from "@capture/shared";
import { useZoneEditorStore } from "../../state/zone-editor-store.js";

interface MarkGroupsPanelProps {
  formId: string;
  /** The active form's current `markGroups` declarations (draft content). */
  markGroups: NonNullable<FormContent["markGroups"]>;
  /** Writes the patched declarations back onto the form (form-config store). */
  onUpdateForm: (formId: string, patch: Partial<FormContent>) => void;
}

const MODES: MarkGroupMode[] = ["ExactlyOne", "AtMostOne", "AnyMarked"];

/**
 * Marksense v2 — mode declarations for the mark groups referenced by the
 * active form's zones. Groups are DERIVED from zone membership (a group
 * exists because zones reference it); this panel only assigns each its
 * resolution mode. An undeclared-but-referenced group shows the ExactlyOne
 * default and declares itself on first change. Renders nothing when no zone
 * references a group. Same-kind membership is enforced at publish
 * (validateMarkZoneConfig) — this panel is mode-only by design.
 */
export function MarkGroupsPanel({ formId, markGroups, onUpdateForm }: MarkGroupsPanelProps) {
  const zoneMappings = useZoneEditorStore((s) => s.zoneMappings);

  const referencedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const zm of zoneMappings) {
      if (zm.zone?.markGroupId) ids.add(zm.zone.markGroupId);
    }
    return [...ids].sort();
  }, [zoneMappings]);

  if (referencedIds.length === 0) return null;

  const declared = new Map(markGroups.map((g) => [g.id, g.mode]));

  const setMode = (id: string, mode: MarkGroupMode) => {
    const next = referencedIds.map((gid) => ({
      id: gid,
      mode: gid === id ? mode : (declared.get(gid) ?? "ExactlyOne"),
    }));
    // Preserve declarations for groups not currently referenced (a zone may
    // be mid-edit) so a transient state never silently drops a mode.
    for (const g of markGroups) {
      if (!referencedIds.includes(g.id)) next.push(g);
    }
    onUpdateForm(formId, { markGroups: next });
  };

  return (
    <section className="mark-groups-panel" aria-label="Mark groups">
      <header className="mgp-header">
        <h3 className="mgp-title">Mark Groups</h3>
      </header>
      <ul className="mgp-list">
        {referencedIds.map((id) => (
          <li key={id} className="mgp-row">
            <span className="mgp-id" title={id}>{id}</span>
            <select
              value={declared.get(id) ?? "ExactlyOne"}
              aria-label={`Resolution mode for mark group ${id}`}
              onChange={(e) => setMode(id, e.target.value as MarkGroupMode)}
            >
              {MODES.map((m) => (
                <option key={m} value={m}>
                  {m === "ExactlyOne" ? "Exactly one" : m === "AtMostOne" ? "At most one" : "Any marked"}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
      <p className="mgp-hint">
        All zones in a group must share one mark kind — mixed kinds block publish.
      </p>

      <style>{`
        .mark-groups-panel {
          margin-top: var(--space-2);
          padding: var(--space-2);
          border-top: 1px solid var(--color-border);
        }
        .mgp-title {
          margin: 0 0 var(--space-1);
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-muted);
        }
        .mgp-list { list-style: none; margin: 0; padding: 0; }
        .mgp-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-2);
          padding: 2px 0;
          font-size: 0.75rem;
        }
        .mgp-id {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .mgp-hint {
          margin: var(--space-1) 0 0;
          font-size: 0.65rem;
          color: var(--color-text-muted);
        }
      `}</style>
    </section>
  );
}
